import React from 'react';
import { Star, MapPin, Clock, ShieldCheck, MessageCircle, Eye } from 'lucide-react';
import { ServiceProvider } from '@/types';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { StatusBadge, OnlineStatus } from '@/components/ui/StatusIndicator';

interface ProviderCardProps {
    provider: ServiceProvider;
    onRequestQuote?: (providerId: string) => void;
    onViewProfile?: (providerId: string) => void;
    onMessage?: (providerId: string) => void;
}

/**
 * Professional Provider Card Component
 * Clean, trustworthy design for displaying service provider information
 */
export const ProviderCard: React.FC<ProviderCardProps> = ({
    provider,
    onRequestQuote,
    onViewProfile,
    onMessage
}) => {
    const isAvailable = provider.availabilityStatus === 'AVAILABLE';
    const hourlyRate = provider.pricing?.hourlyRate;

    return (
        <Card hover padding="lg">
            {/* Header with Avatar and Name */}
            <div className="flex items-start gap-4 mb-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {provider.profileImage ? (
                        <img
                            src={provider.profileImage}
                            alt={provider.businessName}
                            className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                        />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center border-2 border-gray-100">
                            <span className="text-xl font-bold text-primary-600">
                                {provider.businessName.charAt(0)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Name and Rating */}
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                        {provider.businessName}
                    </h3>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center">
                            <Star className="w-4 h-4 text-warning-500 fill-current" />
                            <span className="ml-1 text-sm font-medium text-gray-900">
                                {provider.rating?.toFixed(1) || '5.0'}
                            </span>
                        </div>
                        <span className="text-sm text-gray-500">
                            ({provider.reviewCount || 0} reviews)
                        </span>
                    </div>

                    {/* Provider Type */}
                    <div className="text-xs text-gray-600">
                        {provider.providerType === 'COMPANY' ? 'Company' : 'Individual Professional'}
                    </div>
                </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
                {provider.verified && (
                    <StatusBadge 
                        variant="success" 
                        label="Verified Pro" 
                        icon={ShieldCheck}
                        size="sm"
                    />
                )}
                {isAvailable && (
                    <StatusBadge 
                        variant="info" 
                        label="Available Today" 
                        icon={Clock}
                        size="sm"
                        pulse
                    />
                )}
                {provider.rating && provider.rating >= 4.5 && provider.reviewCount && provider.reviewCount >= 50 && (
                    <StatusBadge 
                        variant="warning" 
                        label="Top Rated" 
                        icon={Star}
                        size="sm"
                    />
                )}
            </div>

            {/* Services/Specialties */}
            {provider.serviceCategories && provider.serviceCategories.length > 0 && (
                <div className="mb-4">
                    <p className="text-xs font-medium text-gray-600 mb-2">Specialties:</p>
                    <div className="flex flex-wrap gap-2">
                        {provider.serviceCategories.slice(0, 3).map((service: string, index: number) => (
                            <span
                                key={index}
                                className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                            >
                                {service}
                            </span>
                        ))}
                        {provider.serviceCategories.length > 3 && (
                            <span className="inline-block px-2 py-1 text-xs text-gray-500">
                                +{provider.serviceCategories.length - 3} more
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Bio/Description */}
            {provider.bio && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {provider.bio}
                </p>
            )}

            {/* Location and Price */}
            <div className="flex items-center justify-between text-sm mb-4 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-1 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{provider.location || 'Ireland'}</span>
                </div>
                {hourlyRate && (
                    <div className="font-semibold text-gray-900">
                        €{hourlyRate}/hour
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
                {onRequestQuote && (
                    <Button
                        variant="primary"
                        size="md"
                        fullWidth
                        onClick={() => onRequestQuote(provider.id)}
                    >
                        Request Quote
                    </Button>
                )}
                {onViewProfile && (
                    <Button
                        variant="outline"
                        size="md"
                        onClick={() => onViewProfile(provider.id)}
                        leftIcon={<Eye className="w-4 h-4" />}
                    >
                        View Profile
                    </Button>
                )}
                {onMessage && (
                    <Button
                        variant="ghost"
                        size="md"
                        onClick={() => onMessage(provider.id)}
                        leftIcon={<MessageCircle className="w-4 h-4" />}
                        aria-label="Send message to provider"
                    >
                        Message
                    </Button>
                )}
            </div>

            {/* Years in Business */}
            {provider.yearsOfExperience && provider.yearsOfExperience > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center">
                        {provider.yearsOfExperience} {provider.yearsOfExperience === 1 ? 'year' : 'years'} of experience
                    </p>
                </div>
            )}
        </Card>
    );
};

/**
 * Compact Provider Card for list views
 */
export const ProviderCardCompact: React.FC<ProviderCardProps> = ({
    provider,
    onRequestQuote,
    onViewProfile
}) => {
    const hourlyRate = provider.pricing?.hourlyRate;

    return (
        <Card hover padding="md">
            <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {provider.profileImage ? (
                        <img
                            src={provider.profileImage}
                            alt={provider.businessName}
                            className="w-12 h-12 rounded-full object-cover border border-gray-200"
                        />
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-lg font-bold text-primary-600">
                                {provider.businessName.charAt(0)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate">
                                {provider.businessName}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center text-sm">
                                    <Star className="w-3.5 h-3.5 text-warning-500 fill-current" />
                                    <span className="ml-1 font-medium">{provider.rating?.toFixed(1) || '5.0'}</span>
                                </div>
                                {provider.verified && (
                                    <ShieldCheck className="w-4 h-4 text-success-600" />
                                )}
                            </div>
                        </div>
                        {hourlyRate && (
                            <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                €{hourlyRate}/hr
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex-shrink-0 flex gap-2">
                    {onViewProfile && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewProfile(provider.id)}
                        >
                            View
                        </Button>
                    )}
                    {onRequestQuote && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onRequestQuote(provider.id)}
                        >
                            Quote
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
};
